import { BellOutlined, CheckOutlined } from "@ant-design/icons";
import { useAuth0 } from "@auth0/auth0-react";
import { Badge, Button, List, Popover, Spin, Typography, message } from "antd";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAllNotifications } from "../store/common";
import {
  readAllNotification,
  readSingleNotification,
} from "../utils/notifications";

const { Text } = Typography;

const NotificationPopover = () => {
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getIdTokenClaims } = useAuth0();

  // Get notifications from Redux store
  const allNotifications = useSelector(
    (state) => state.commonReducer.allNotifications || []
  );

  // Update notification count whenever allNotifications changes
  useEffect(() => {
    if (allNotifications && allNotifications.length > 0) {
      const count = allNotifications.filter(
        (notification) => notification.is_read === "0"
      ).length;
      setNotificationCount(count);
    }
  }, [allNotifications]);

  // Get only the first 10 notifications for the popover
  const recentNotifications = allNotifications.slice(0, 10);

  // Improved handler for "Mark all as read" button
  const handleMarkAllRead = async () => {
    try {
      setLoading(true);
      const token = await getIdTokenClaims();
      // Call API to mark all as read
      const response = await readAllNotification(token.__raw);
      if (response.data.success) {
        // Update Redux store through dispatch
        dispatch(
          setAllNotifications(
            allNotifications.map((item) =>
              item.is_read === "0" ? { ...item, is_read: "1" } : item
            )
          )
        );
        // Reset local notification count immediately for UI responsiveness
        setNotificationCount(0);
      } else {
        const errorMsg =
          response.data.error || "Failed to mark notifications as read";
        message.error(errorMsg);
        console.error("API Error:", errorMsg);
      }
    } catch (error) {
      // Handle exceptions with user-friendly message
      const errorMsg =
        error.response?.data?.error ||
        "Something went wrong. Please try again.";
      message.error(errorMsg);
      console.error("Exception in handleMarkAllRead:", error);
    } finally {
      // Always end loading state, even on error
      setLoading(false);
    }
  };

  // Handler for "View all" button
  const handleViewAll = () => {
    setIsNotificationOpen(false);
    navigate("/settings/notifications");
  };

  // Get status indicator color based on notification type
  const getStatusColor = (notification) => {
    const isRead = notification.is_read || "";

    if (isRead == 1) {
      return "bg-gray-500";
    }
    return "bg-green-500";
  };

  // Format the timestamp to a more readable format
  const formatTime = (timestamp) => {
    return timestamp;
  };

  // Handler to mark a single notification as read when clicked
  const handleNotificationClick = async (notification) => {
    if (notification.is_read === "1") return; // If already marked as read, do nothing

    try {
      setLoading(true);
      const token = await getIdTokenClaims();
      const response = await readSingleNotification(
        token.__raw,
        notification.id
      );
      if (response.data.success) {
        dispatch(
          setAllNotifications(
            allNotifications.map((item) =>
              item.id === notification.id ? { ...item, is_read: "1" } : item
            )
          )
        );
        // Update notification count
        setNotificationCount((prevCount) => prevCount - 1);
      } else {
        const errorMsg =
          response.data.error || "Failed to mark notification as read";
        message.error(errorMsg);
        console.error("API Error:", errorMsg);
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.error ||
        "Something went wrong. Please try again.";
      message.error(errorMsg);
      console.error("Exception in handleNotificationClick:", error);
    } finally {
      // Always end loading state, even on error
      setLoading(false);
    }
  };

  // Content for the notification popover
  const notificationContent = (
    <div className="w-80 md:w-96 max-h-[500px] overflow-hidden">
      <div className="flex justify-between items-center border-b border-gray-200 px-4 py-3">
        <Text strong className="text-lg">
          Notifications
        </Text>
        <Button
          type="text"
          size="small"
          icon={<CheckOutlined />}
          onClick={handleMarkAllRead}
          disabled={loading || notificationCount === 0}
          className="flex items-center"
        >
          Mark all as read
        </Button>
      </div>

      <div className="overflow-y-auto max-h-[400px]">
        {loading ? (
          <div className="py-8 flex justify-center">
            <Spin />
          </div>
        ) : recentNotifications.length === 0 ? (
          <div className="py-8 text-center text-gray-500">No notifications</div>
        ) : (
          <List
            dataSource={recentNotifications}
            renderItem={(item) => (
              <List.Item
                className="border-b hover:bg-gray-50 cursor-pointer"
                style={{ padding: "12px 16px" }}
                onClick={() => handleNotificationClick(item)} // Add the click handler here
              >
                <div className="flex w-full">
                  <div className="mr-3 mt-1.5">
                    <div
                      className={`w-3 h-3 rounded-full ${getStatusColor(item)}`}
                    ></div>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <Text strong>{item.type}</Text>
                      <Text type="secondary" className="text-xs ml-2">
                        {formatTime(item.created_at)}
                      </Text>
                    </div>
                    <div className="text-sm text-gray-500">
                      {parse(item.message)}
                    </div>
                  </div>
                </div>
              </List.Item>
            )}
          />
        )}
      </div>

      <div className="border-t border-gray-200 py-2 text-center">
        <Button type="link" onClick={handleViewAll} className="h-auto py-1">
          View all
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={notificationContent}
      title={null}
      trigger="click"
      open={isNotificationOpen}
      onOpenChange={setIsNotificationOpen}
      placement="bottomRight"
      overlayStyle={{ width: "auto" }}
      overlayInnerStyle={{ padding: 0 }}
      arrow={false}
    >
      <div className="cursor-pointer">
        <Badge color="blue" count={notificationCount} size="middle">
          <BellOutlined className="text-2xl" />
        </Badge>
      </div>
    </Popover>
  );
};

export default NotificationPopover;
